// Everything frames 07/08/09/10 branch on.
//
// THE ONE DISTINCTION THIS FILE EXISTS TO PROTECT (Wave 1 cost a correction
// getting it right): "signed out" and "this org has no documents" are not the
// same answer and must not render the same screen.
//
//   GET /docs -> 401 not_signed_in    => no credential at all  -> frame 10 / signin
//   GET /docs -> 200 with total: 0    => authenticated, no docs -> frame 09 empty
//   GET /docs -> 200, every row       => docs exist, extraction status cannot be
//                status "unavailable"    determined (no model key) -> NOT empty
//
// The session cookie is HttpOnly, so sign-in state is knowable only by asking
// the server. Never inspect `document.cookie`.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getKey } from "../../dashboard/api";

// Mirrors `dashboard/api.ts` — that module keeps its base private. `polish`
// should hoist one shared client rather than let a third copy appear.
const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

/** The analytics window every number on this screen is measured over. */
export const WINDOW_DAYS = 30;

function endpoint(path: string): string {
  return new URL(
    `${API_BASE}${path}`,
    typeof window !== "undefined" ? window.location.origin : undefined,
  ).toString();
}

// Both credentials are offered: the cookie is the human one, the key the
// machine one. `GET /docs` accepts either; the `/dashboards/*` routes take only
// the key.
//
// `same-origin`, NOT `include`, and the difference is not cosmetic. The API
// sets `allow_credentials=False` on its CORS middleware (`backend/api/app.py`),
// so a cross-origin fetch carrying `include` is rejected by the browser before
// the response is ever read — every panel would go dark. Deployed, the SPA
// reaches the API same-origin through Caddy's `/api`, so the session cookie
// rides exactly as intended; in a split-port local dev the cookie is simply
// absent and the license key carries the request.
//
// FOR `polish` / a backend owner: making the cookie work cross-origin needs
// `allow_credentials=True` plus the SPA's dev origin in
// `embed_allowed_origins`. That is backend config, not screen code.
function request(path: string): Promise<Response> {
  const key = getKey();
  return fetch(endpoint(path), {
    credentials: "same-origin",
    headers: key
      ? { Accept: "application/json", "X-mAiSight-Key": key }
      : { Accept: "application/json" },
  });
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await request(path);
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

export type Me = {
  email: string;
  org_slug: string;
  // Operator flags, computed server-side in GET /auth/me and nowhere else.
  // Optional because /auth/consume returns the same model without them, and
  // because a stale cached response must read as "not staff" rather than as
  // `undefined` leaking into a truthiness check.
  is_staff?: boolean;
  // Exactly `is_staff && org_slug !== STAFF_ORG`: a staff session is either at
  // home or viewing a tenant, so no session column is needed to know which.
  impersonating?: boolean;
};

export function useMe(): UseQueryResult<Me | null, Error> {
  return useQuery<Me | null, Error>({
    queryKey: ["auth-me"],
    queryFn: async () => {
      const res = await request("/auth/me");
      if (res.status === 401) return null;
      if (!res.ok) throw new Error(`GET /auth/me failed: ${res.status}`);
      return (await res.json()) as Me;
    },
  });
}

export type AccessState = "checking" | "ok" | "denied" | "signed-out";

/**
 * A session names its own org, so a slug it does not match is a dead end
 * (frame 10). A license key is the MACHINE credential and is never a UI
 * access answer for a browsing human — `require_org` now accepts a session
 * directly (backend/api/security.py), so the browser gate needs nothing
 * beyond the session itself. There is also no UI left that could put a key
 * in this browser's `localStorage` on a human's behalf: `KeyField` is
 * deleted and stays deleted (PRD 18 §8), so a `hasKey` branch here would
 * only ever fire on a stale leftover value, never on a real signed-out
 * visitor.
 *
 * Pure, and exported, so all three outcomes are testable without a fetch:
 * the signed-out/denied split is the one this PRD says must not collapse.
 */
export function decideAccess(
  orgSlug: string,
  me: Me | null | undefined,
): Exclude<AccessState, "checking"> {
  if (me) return me.org_slug === orgSlug ? "ok" : "denied";
  return "signed-out";
}

export function useOrgAccess(orgSlug: string): AccessState {
  const me = useMe();
  if (me.isPending) return "checking";
  return decideAccess(orgSlug, me.data);
}

/**
 * Sign-out lands on `/signin` by a full document load rather than a client
 * navigation: after `POST /auth/logout` deletes the session row, every cached
 * query in memory holds answers the old session gave. Throwing the document
 * away is the cheapest way to be sure none of it survives.
 *
 * `polish` owns US-152 and should bind this once inside `AppShell`; today it is
 * passed explicitly by the two routes this agent owns.
 */
export function signOut(): void {
  void request("/auth/logout").finally(() => {
    window.location.assign("/signin");
  });
}

export type DocStatus =
  | "not_extracted"
  | "extracted"
  | "published"
  | "unavailable";

export interface DocItem {
  doc_id: string;
  title: string | null;
  source_kind: string | null;
  corpus_id: string | null;
  term_count: number;
  status: DocStatus;
}

export interface DocsPage {
  items: DocItem[];
  total: number;
  limit: number;
  offset: number;
}

const DOCS_LIMIT = 200; // the endpoint's ceiling; 60 seeded.

export function useDocs(orgSlug: string): UseQueryResult<DocsPage, Error> {
  return useQuery<DocsPage, Error>({
    queryKey: ["overview-docs", orgSlug],
    queryFn: () => getJSON<DocsPage>(`/docs?limit=${DOCS_LIMIT}`),
  });
}

export interface Sufficiency {
  verdict: "sufficient" | "insufficient";
  documents: number;
  terms: number;
  sessions: number;
  thresholds: { documents: number; terms: number; sessions: number };
  reasons: string[];
  unexplained_terms: number;
  unexplained_examples: string[];
}

/** 404 means this credential cannot reach the keyed analytics routes — an
 *  absent number, never a failed screen. */
export function useSufficiency(
  orgSlug: string,
): UseQueryResult<Sufficiency | null, Error> {
  return useQuery<Sufficiency | null, Error>({
    queryKey: ["overview-sufficiency", orgSlug, WINDOW_DAYS],
    queryFn: async () => {
      const path = `/dashboards/${orgSlug}/sufficiency?days=${WINDOW_DAYS}`;
      const res = await request(path);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
      return (await res.json()) as Sufficiency;
    },
  });
}

export type DocsVerdict = "has-documents" | "no-documents" | "status-unknown";

/** `total: 0` and "every row is `unavailable`" are different answers, and
 *  US-053 exists so they can be told apart. Rendering "no documents" for the
 *  second is the defect. */
export function docsVerdict(page: DocsPage): DocsVerdict {
  if (page.total === 0) return "no-documents";
  if (page.items.length > 0 && page.items.every((d) => d.status === "unavailable"))
    return "status-unknown";
  return "has-documents";
}
